import React from 'react';

export interface GraphicsRendererData {
  shape?: string;
  fillColor?: string;
  fillAlpha?: number;
  strokeColor?: string;
  strokeWidth?: number;
  strokeAlpha?: number;
  width?: number;
  height?: number;
  radius?: number;
  cornerRadius?: number;
  points?: number;
}

/**
 * Generate polygon points (regular polygon) inscribed in an ellipse.
 * cx, cy = center; rx, ry = radii; n = number of sides.
 * Rotation starts from top (negative y-axis).
 */
function regularPolygonPoints(cx: number, cy: number, rx: number, ry: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    pts.push(`${cx + rx * Math.cos(angle)},${cy + ry * Math.sin(angle)}`);
  }
  return pts.join(' ');
}

/**
 * Generate star points inscribed in an ellipse.
 * Outer radii = rx, ry; Inner radii = rx*0.4, ry*0.4.
 * n = number of outer points (total vertices = 2*n).
 */
function starPoints(cx: number, cy: number, rx: number, ry: number, n: number): string {
  const pts: string[] = [];
  const innerRatio = 0.4;
  const totalPts = n * 2;
  for (let i = 0; i < totalPts; i++) {
    const angle = (Math.PI * 2 * i) / totalPts - Math.PI / 2;
    const isOuter = i % 2 === 0;
    const rxi = isOuter ? rx : rx * innerRatio;
    const ryi = isOuter ? ry : ry * innerRatio;
    pts.push(`${cx + rxi * Math.cos(angle)},${cy + ryi * Math.sin(angle)}`);
  }
  return pts.join(' ');
}

interface GraphicsSvgRendererProps {
  data: GraphicsRendererData;
  /** Rendered width of the SVG element in pixels (already scaled) */
  renderWidth: number;
  /** Rendered height of the SVG element in pixels (already scaled) */
  renderHeight: number;
}

/**
 * Renders a graphics shape as an SVG element.
 * Fills the entire renderWidth x renderHeight area.
 * Used by both SceneCanvas (editor) and GameViewport (live preview).
 */
export const GraphicsSvgRenderer: React.FC<GraphicsSvgRendererProps> = ({
  data,
  renderWidth,
  renderHeight,
}) => {
  const shape = data.shape ?? 'rectangle';
  const fillColor = data.fillColor ?? '#ffffff';
  const fillAlpha = data.fillAlpha ?? 1;
  const strokeColor = data.strokeColor ?? '#000000';
  const strokeWidth = data.strokeWidth ?? 0;
  const strokeAlpha = data.strokeAlpha ?? 1;
  const cornerRadius = data.cornerRadius ?? 0;
  const numPoints = data.points ?? 5;

  // SVG viewBox matches the component's logical size (width/height from data)
  // so shapes are drawn in logical space and the SVG scales to renderWidth x renderHeight.
  const logicalW = data.width ?? 100;
  const logicalH = data.height ?? 100;
  const logicalR = data.radius ?? 50;

  // For shapes that need a viewBox (all shapes), we use the logical dimensions
  // For circle/ellipse/star/polygon, the viewBox is based on the shape's bounding box
  let viewBoxW: number;
  let viewBoxH: number;

  switch (shape) {
    case 'circle':
      viewBoxW = logicalR * 2;
      viewBoxH = logicalR * 2;
      break;
    case 'ellipse':
      viewBoxW = logicalW;
      viewBoxH = logicalH;
      break;
    case 'star':
    case 'polygon':
      // Use the larger of width/height to make a square viewBox for uniform shape
      viewBoxW = logicalW;
      viewBoxH = logicalH;
      break;
    case 'line':
      viewBoxW = logicalW;
      viewBoxH = logicalH;
      break;
    default:
      // rectangle, rounded-rectangle
      viewBoxW = logicalW;
      viewBoxH = logicalH;
      break;
  }

  // Account for stroke in viewBox so strokes aren't clipped
  const sw = strokeWidth;
  const vbX = -sw / 2;
  const vbY = -sw / 2;
  const vbW = viewBoxW + sw;
  const vbH = viewBoxH + sw;

  const fill = `rgba(${hexToRgb(fillColor)}, ${fillAlpha})`;
  const stroke = sw > 0 ? `rgba(${hexToRgb(strokeColor)}, ${strokeAlpha})` : 'none';

  let shapeElement: React.ReactNode;

  switch (shape) {
    case 'rectangle':
      shapeElement = (
        <rect
          x={0} y={0}
          width={logicalW} height={logicalH}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'rounded-rectangle':
      shapeElement = (
        <rect
          x={0} y={0}
          width={logicalW} height={logicalH}
          rx={cornerRadius} ry={cornerRadius}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'circle':
      shapeElement = (
        <circle
          cx={logicalR} cy={logicalR} r={logicalR}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'ellipse':
      shapeElement = (
        <ellipse
          cx={logicalW / 2} cy={logicalH / 2}
          rx={logicalW / 2} ry={logicalH / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'polygon':
      shapeElement = (
        <polygon
          points={regularPolygonPoints(logicalW / 2, logicalH / 2, logicalW / 2, logicalH / 2, numPoints)}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'star':
      shapeElement = (
        <polygon
          points={starPoints(logicalW / 2, logicalH / 2, logicalW / 2, logicalH / 2, numPoints)}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;

    case 'line':
      shapeElement = (
        <line
          x1={0} y1={logicalH / 2}
          x2={logicalW} y2={logicalH / 2}
          stroke={sw > 0 ? stroke : fill}
          strokeWidth={sw > 0 ? sw : 2}
        />
      );
      break;

    default:
      shapeElement = (
        <rect
          x={0} y={0}
          width={logicalW} height={logicalH}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;
  }

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={renderWidth}
      height={renderHeight}
      viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {shapeElement}
    </svg>
  );
};

/** Convert hex color string to "r, g, b" string for use in rgba() */
function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}
